import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, selectCurrentUser } from '../../features/auth/authSlice';
import { 
  BuildingOfficeIcon, 
  DocumentTextIcon, 
  HomeIcon, 
  ArrowLeftOnRectangleIcon,
  UserCircleIcon,
  UserPlusIcon,
  Bars3Icon,
  XMarkIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { ThemeToggle } from '../ui/theme-toggle';
import { Logger } from '../../lib/utils';

export const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  return (
    <div className="h-screen bg-background overflow-hidden flex flex-col">
      {/* Barra superior */}
      <header className="bg-card border-b border-border h-16 z-30 shadow-md px-4 md:px-6 flex-shrink-0">
        <div className="flex items-center justify-between h-full">
          {/* Logo y toggle */}
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md text-foreground hover:bg-accent hover:text-accent-foreground transition-colors focus:outline-none"
              aria-label="Toggle menu"
            >
              {sidebarOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
            </button>
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-primary rounded-md p-1.5 shadow-sm">
                <BuildingOfficeIcon className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-semibold text-lg text-foreground hidden md:block">Empresas App</span>
            </Link>
          </div>
          
          {/* Menú Usuario */}
          <UserMenu />
        </div>
      </header>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - fijo en móviles, integrado en escritorio */}
        <aside 
          className={`fixed inset-y-0 pt-16 left-0 z-20 w-64 transform transition-transform duration-300 ease-in-out 
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
            lg:pt-0 lg:static lg:translate-x-0 ${sidebarOpen ? 'lg:block' : 'lg:hidden'}`}
        >
          <div className="h-full bg-card border-r border-border shadow-sm overflow-y-auto">
            <SidebarContent closeSidebar={() => setSidebarOpen(false)} />
          </div>
        </aside>
        
        {/* Overlay para cerrar sidebar en móviles */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-10 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Contenido principal - Expandible*/}
        <main className={`flex-1 overflow-auto p-4 sm:p-6 md:p-8 transition-all duration-300 ease-in-out w-full bg-background`}>
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

// Menú de usuario en la cabecera
const UserMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const [menuOpen, setMenuOpen] = useState(false);
  
  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };
  
  // Función para obtener la etiqueta de rol según el tipo de usuario
  const getRoleLabel = (role?: string): string => {
    if (!role) return 'Usuario';
    
    switch (role) {
      case 'superadmin':
        return 'Superadministrador';
      case 'admin':
        return 'Administrador';
      case 'user':
        return 'Usuario de empresa';
      default:
        return 'Usuario';
    }
  };
  
  return (
    <div className="relative flex items-center gap-2">
      <ThemeToggle />
      
      <button 
        className="flex items-center gap-2 p-2 rounded-full hover:bg-accent transition-colors"
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium shadow-sm">
          {user?.email?.charAt(0).toUpperCase() || 'U'}
        </div>
        <span className="text-sm font-medium text-foreground hidden md:block">
          {user?.email?.split('@')[0] || 'Usuario'}
        </span>
      </button>
      
      {menuOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setMenuOpen(false)}
          ></div>
          <div className="absolute right-0 mt-2 w-64 bg-card rounded-lg shadow-sm border border-border z-50 top-full">
            <div className="p-4 border-b border-border bg-muted">
              <p className="text-sm font-medium text-foreground">{user?.email}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {getRoleLabel(user?.role)}
              </p>
            </div>
            <div className="p-2">
              <Link 
                to="/perfil" 
                className="flex items-center gap-2 p-2.5 text-sm text-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                <UserCircleIcon className="h-5 w-5 text-muted-foreground" />
                Mi Perfil
              </Link>
              <button 
                onClick={handleLogout}
                className="w-full flex items-center gap-2 p-2.5 text-sm text-destructive hover:bg-destructive/10 rounded-md transition-colors mt-1"
              >
                <ArrowLeftOnRectangleIcon className="h-5 w-5" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Contenido de la barra lateral
interface SidebarContentProps {
  closeSidebar: () => void;
}

const SidebarContent = ({ closeSidebar }: SidebarContentProps) => {
  const user = useSelector(selectCurrentUser);
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdminOrSuperAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Usar Logger en lugar de console.log
  Logger.debug('Estado del usuario en Sidebar:', {
    role: user?.role,
    empresa_id: user?.empresa_id,
    clientes_enabled: (user as any)?.clientes_enabled,
    isAdminOrSuperAdmin,
    isSuperAdmin,
    bot1_enabled: (user as any)?.bot1_enabled,
    bot2_enabled: (user as any)?.bot2_enabled,
    bot3_enabled: (user as any)?.bot3_enabled,
    bot4_enabled: (user as any)?.bot4_enabled,
    bot5_enabled: (user as any)?.bot5_enabled,
    bot6_enabled: (user as any)?.bot6_enabled,
    bot7_enabled: (user as any)?.bot7_enabled,
    bot8_enabled: (user as any)?.bot8_enabled,
    bot9_enabled: (user as any)?.bot9_enabled,
    bot10_enabled: (user as any)?.bot10_enabled
  });

  return (
    <div className="py-6 px-3">
      <nav className="space-y-6">
        {/* Navegación principal */}
        <div>
          <h3 className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Principal
          </h3>
          <div className="mt-3 space-y-1">
            <NavItem to="/" icon={HomeIcon} text="Dashboard" closeSidebar={closeSidebar} />
          </div>
        </div>

        {/* Sección de administración (Empresas, Usuarios) - Solo visible para admin y superadmin */}
        {isAdminOrSuperAdmin && (
          <div>
            <h3 className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Administración
            </h3>
            <div className="mt-3 space-y-1">
              {isSuperAdmin && (
                <NavItem to="/empresas" icon={BuildingOfficeIcon} text="Empresas" closeSidebar={closeSidebar} />
              )}
              {/* Usuarios aquí porque es solo para admins */}
              <NavItem to="/usuarios" icon={UserPlusIcon} text="Usuarios" closeSidebar={closeSidebar} />
            </div>
          </div>
        )}

        {/* Sección de Gestión (Clientes) - Visible si tiene permiso */}
        {user?.clientes_enabled && (
          <div>
            {/* Solo mostrar título si no es admin/superadmin (para evitar duplicar título) */}
            {!isAdminOrSuperAdmin && (
              <h3 className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Gestión
              </h3>
            )}
            <div className="mt-3 space-y-1">
              <NavItem to="/clientes" icon={UsersIcon} text="Clientes" closeSidebar={closeSidebar} />
            </div>
          </div>
        )}

        {/* Sección de configuración */}
        <div>
          <h3 className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Configuración
          </h3>
          <div className="mt-3 space-y-1">
            <NavItem to="/textos" icon={DocumentTextIcon} text="Textos" closeSidebar={closeSidebar} />
            <NavItem to="/perfil" icon={UserCircleIcon} text="Mi Perfil" closeSidebar={closeSidebar} />
          </div>
        </div>
      </nav>
      
      {/* Footer con versión */}
      <div className="mt-auto">
        <div className="border-t border-border pt-4">
          <p className="text-xs text-muted-foreground text-center">
            Empresas App v1.0
          </p>
        </div>
      </div>
    </div>
  );
};

// Item de navegación en la sidebar
interface NavItemProps {
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  text: string;
  badge?: string;
  closeSidebar: () => void;
}

const NavItem = ({ to, icon: Icon, text, badge, closeSidebar }: NavItemProps) => {
  // Cerrar el sidebar en dispositivos móviles cuando se hace clic en un enlace
  const handleClick = () => {
    if (window.innerWidth < 1024) {
      closeSidebar();
    }
  };

  return (
    <NavLink 
      to={to} 
      className={({ isActive }) => 
        `flex items-center justify-between px-4 py-3 rounded-md transition-colors ${
          isActive 
            ? 'bg-accent text-accent-foreground font-medium' 
            : 'text-foreground hover:bg-muted'
        }`
      }
      onClick={handleClick}
    >
      {({ isActive }) => (
        <>
          <div className="flex items-center">
            <Icon className={`h-5 w-5 mr-3 flex-shrink-0 ${
              isActive ? 'text-primary' : 'text-muted-foreground'
            }`} />
            <span className="text-sm">{text}</span>
          </div>
          
          {badge && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}; 